import React, { useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { emitDataChange } from "../lib/dataSync";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { UserPlus, CheckCircle } from "lucide-react";

export default function AdminCreateUserTab({ onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", phone: "", password: "", role: "client", plan_id: "normal", months: 12 });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // v2.64 — Trim email & password client-side so what admin sees in the
      // success banner (last.password) matches what was actually sent and
      // hashed. Prevents pasted whitespace from breaking subsequent logins.
      const cleanEmail = (form.email || "").trim();
      const cleanPassword = (form.password || "").trim();
      const r = await api.post("/admin/create-user", {
        ...form,
        email: cleanEmail,
        password: cleanPassword,
        phone: (form.phone || "").trim(),
        plan_id: form.role === "client" ? (form.plan_id === "none" ? null : form.plan_id) : null,
        months: Number(form.months) || 12,
      });
      toast.success(`${form.role.charAt(0).toUpperCase() + form.role.slice(1)} created: ${r.data.user.email}`);
      setLast({ ...form, email: cleanEmail, password: cleanPassword, created_at: new Date().toISOString() });
      setForm({ email: "", name: "", phone: "", password: "", role: "client", plan_id: "normal", months: 12 });
      emitDataChange(["users", "dashboard"]);
      onCreated?.();
    } catch (err) { toast.error(apiErrorMsg(err, "Create failed")); }
    setBusy(false);
  };

  return (
    <div className="max-w-xl">
      <form onSubmit={submit} className="bg-white border border-[#E4E4E7] rounded-sm p-6 space-y-4" data-testid="create-user-form">
        <div className="flex items-center gap-2">
          <UserPlus size={18} className="text-[#0055FF]" />
          <h3 className="font-heading font-semibold text-xl tracking-tight">Create offline account</h3>
        </div>
        <p className="text-[12px] text-[#52525B]">
          For clients you onboard offline (WhatsApp, in-person). You set their password and optionally assign a plan — they can log in immediately.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <F label="Role">
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger className="rounded-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="dealer">Dealer</SelectItem>
                <SelectItem value="builder">Builder / Owner</SelectItem>
                <SelectItem value="team">Team (Internal)</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Name">
            <Input required value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-sm" data-testid="new-user-name" />
          </F>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Email">
            <Input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-sm" data-testid="new-user-email" />
          </F>
          <F label="Password">
            <Input
              required
              minLength={6}
              type="password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              className="rounded-sm"
              placeholder="min 6 chars"
              data-testid="new-user-password"
            />
          </F>
        </div>
        <F label={(form.role === "dealer" || form.role === "builder") ? "Phone (required, 10 digits)" : "Phone (optional)"}>
          <Input
            type="tel"
            required={form.role === "dealer" || form.role === "builder"}
            value={form.phone}
            onChange={(e) => update("phone", e.target.value.replace(/[^\d+\s-]/g, ""))}
            className="rounded-sm"
            placeholder="+91 98xxxxxxxx"
            data-testid="new-user-phone"
          />
        </F>

        {form.role === "client" && (
          <div className="grid grid-cols-2 gap-3">
            <F label="Assign plan">
              <Select value={form.plan_id} onValueChange={(v) => update("plan_id", v)}>
                <SelectTrigger className="rounded-sm" data-testid="new-user-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No plan —</SelectItem>
                  <SelectItem value="normal">Normal (₹999)</SelectItem>
                  <SelectItem value="prime">Prime (₹2999)</SelectItem>
                  <SelectItem value="premium">Premium (₹4999)</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Months">
              <Input type="number" min={1} max={60} value={form.months} onChange={(e) => update("months", e.target.value)} className="rounded-sm" data-testid="new-user-months" />
            </F>
          </div>
        )}

        <Button type="submit" disabled={busy} className="btn-brand rounded-sm" data-testid="create-user-submit">
          {busy ? "Creating…" : "Create Account"}
        </Button>

        {last && (
          <div className="mt-3 p-3 bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm text-[12px]" data-testid="create-user-result">
            <div className="flex items-center gap-1.5 text-[#15803D] font-semibold"><CheckCircle size={13} /> Account ready</div>
            <div className="mt-1 text-[#09090B]">
              Share these credentials with the user: <b>{last.email}</b> / <b>{last.password}</b>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

const F = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">{label}</div>
    {children}
  </div>
);
